import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, 
  ListItemText, AppBar, Toolbar, Typography, Divider, 
  IconButton, Avatar, Collapse, useMediaQuery, useTheme, styled,
} from '@mui/material';
import { 
  LogoutOutlined, NotificationsNone, Menu as MenuIcon, 
  ChevronLeft as ChevronLeftIcon, Dashboard as DashIcon, 
  CloudUpload, Business, AdminPanelSettings, Sync
} from '@mui/icons-material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import logoImg from "../assets/app-icon.png";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

// 1. Styled AppBar to handle synchronized auto-sizing
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'white',
  color: '#1A3A3F',
  boxShadow: 'none',
  borderBottom: '1px solid #e2e8f0',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(!isMobile && {
    width: `calc(100% - ${open ? DRAWER_WIDTH : COLLAPSED_WIDTH}px)`,
    marginLeft: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export const Layout = ({ onLogout, selectedOrg, onOpenSelector }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const navigate = useNavigate();
  const location = useLocation();

  const isFleetSection = location.pathname.startsWith('/fleet');

  // Sync state on mobile resize
  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  const handleToggle = () => setIsExpanded(!isExpanded);

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) setIsExpanded(false);
  };

  const NavItem = ({ text, icon, path }) => {
    const isActive = location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
    const showText = isExpanded || isMobile;

    return (
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton 
          selected={isActive}
          onClick={() => handleNav(path)}
          sx={{ 
            minHeight: 48, 
            justifyContent: showText ? 'initial' : 'center', 
            px: 2.5, 
            mx: showText ? 1.5 : 0.5, 
            borderRadius: 1, 
            mb: 0.5,
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.20)', 
              color: '#2fc0cc', 
              //borderLeft: '1px solid #2fc0cc',
              '& .MuiListItemIcon-root': { color: '#2fc0cc' } 
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 0, 
            mr: showText ? 2.5 : 'auto', 
            color: '#f4f9f9',
            justifyContent: 'center'
          }}>
            {icon}
          </ListItemIcon>
          {showText && (
            <ListItemText primary={text} primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem' }} />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden' }}>
      {/* BRANDING: TMS (Dark/Bold) CENTRAL (Thin/White) */}
      <Box sx={{ p: 3, mb: 2, display: 'flex', alignItems: 'center', minHeight: 64 }}>
        <Box sx={{ width: 32, height: 32, mr: isExpanded || isMobile ? 1.5 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logoImg} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>

        <Typography variant="h6" sx={{ fontFamily: 'Raleway', letterSpacing: -0.5, display: 'flex', color:'#2fc0cc' }}>
          <Box component="span" sx={{ fontWeight: 900 }}>TMS</Box>
          {(isExpanded || isMobile) && (
            <Box component="span" sx={{ fontWeight: 300, color: '#f4f9f9', ml: 1 }}>CENTRAL</Box>
          )}
        </Typography>
      </Box>

      <List sx={{ px: 0 }}>
        <NavItem text="Dashboard" icon={<DashIcon />} path="/" />
        <NavItem text="App Releases" icon={<CloudUpload />} path="/releases" />
        <NavItem text="User Management" icon={<AdminPanelSettings />} path="/users" />
        <NavItem text="Fleet Management" icon={<LocalShippingIcon />} path="/fleet" />
      </List>

      {/* CONDITIONAL COMPANY SWITCHER */}
{/* CONDITIONAL COMPANY SWITCHER */}
<Collapse in={isFleetSection && (isExpanded || isMobile)} timeout={300} unmountOnExit>
  <Divider sx={{ my: 2, mx: 2, borderColor: 'rgba(255,255,255,0.30)' }} />

  <Box sx={{ px: 2 }}>
    <ListItemButton
      onClick={onOpenSelector}
      sx={{
        bgcolor: 'rgba(47,192,204,0.18)',
        borderRadius: 2,
        minHeight: 50,
        border: '1px solid rgba(47,192,204,0.5)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          bgcolor: 'rgba(47,192,204,0.28)',
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: 2,
          color: '#2fc0cc',
        }}
      >
        <Sync />
      </ListItemIcon>

      <ListItemText
        primary="Switch Company"
        primaryTypographyProps={{
          fontWeight: 900,
          color: '#2fc0cc',
          fontSize: '0.8rem',
        }}
      />
    </ListItemButton>

    {/* Selected Company */}
    <Box
      sx={{
        mt: 1,
        px: 1.5,
        py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: '0.65rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Selected Company
        </Typography>

        <Typography
          sx={{
            color: '#ffffff',
            fontSize: '0.8rem',
            fontWeight: 700,
            mt: 0.2,
          }}
        >
          {selectedOrg.name}
        </Typography>
      </Box>
    </Box>
  </Box>
</Collapse>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <StyledAppBar position="fixed" open={isExpanded} isMobile={isMobile}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton onClick={handleToggle} edge="start">
            {(isExpanded && !isMobile) ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* <NotificationsNone fontSize="small" sx={{ display: { xs: 'none', sm: 'block' } }} /> */}
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1A3A3F', color: '#fff', fontWeight: 800, fontSize: '0.8rem' }}>AD</Avatar>
            <IconButton onClick={onLogout} color="error"><LogoutOutlined fontSize="small" /></IconButton>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* MOBILE OVERLAY DRAWER */}
      <Drawer
        variant="temporary"
        open={isMobile && isExpanded}
        onClose={handleToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: '#18383d', color: '#f4f9f9', border: 'none' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* DESKTOP PERMANENT MINI-DRAWER */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH, 
            bgcolor: '#18383d', 
            color: '#f4f9f9', 
            overflowX: 'hidden', 
            border: 'none',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 4, md: 2 }, 
          mt: 8,
          width: '100%', 
          overflowX: 'hidden',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};